/**
 * emailPresets.ts — Three layout presets for the email builder ("Templates" menu).
 *
 * Presets are now presentation-only profiles: they change column layout, spacing and
 * optional brand color while leaving ALL text content (header/greeting, Summary
 * labels+values+tokens, Notes, Footer) byte-for-byte unchanged.
 *
 * Language policy: UI descriptions below may be English — no body copy lives here.
 */

import { PresentationProfile } from "./actions";

export interface EmailPreset {
	id: string;
	label: string;
	description: string;
	profile: PresentationProfile;
}

/**
 * Full booking email layout used by the "Standard" preset when the canvas is an
 * empty skeleton (no summary block). The ${...} tokens are literal — they are
 * preserved as-is through TS template literal escaping.
 */
export const STANDARD_FULL_LAYOUT = `[[WL-EMAIL]]
Eyebrow: WhaleLO Booking
Title: Booking Confirmed
Subtitle: Your reservation has been successfully confirmed. Please review the booking details below.

Hello \${customerName},
Your luggage storage is booked. Show the booking code below when you arrive at the station.

[Summary]
# Booking details
Booking code: \${orderId}
Time: \${dropOffDate} - \${pickUpDate}
Luggage: \${quantity} bags
Total!: \${total}
# Storage location
Station: \${displayName}
Hotline: \${facilityHotline1}
Address: \${partnerAddress} | Map: \${mapLink}
[/Summary]

[Notes: Good to know]
- Show your booking code at drop-off.
- Rescheduling is free up to 1 hour before — just reply to this email or call us.
[/Notes]

[Footer]
Title: WhaleLO - Luggage Storage & Delivery
Hotline: (+84) 866 888 431 (WhatsApp, Zalo)
Email: info@whalelo.net
Website: https://whalelo.net|whalelo.net
Note: This is an automated email. Please do not reply directly.
[/Footer]`;

export const EMAIL_PRESETS: EmailPreset[] = [
	{
		id: "standard",
		label: "WhaleLO Standard",
		description: "Single-column layout — keeps all your text, only restyles.",
		profile: { summaryColumns: 1, density: "comfortable", brand: "#005e9e" },
	},
	{
		id: "two-column",
		label: "Two-column compact",
		description: "Pairs adjacent rows side-by-side — keeps all your text, only restyles.",
		profile: { summaryColumns: 2, density: "compact" },
	},
	{
		id: "single-column",
		label: "Single column classic",
		description: "All rows single-column with generous spacing — keeps all your text, only restyles.",
		profile: { summaryColumns: 1, density: "spacious" },
	},
];
